import React from "react";
import { useSelector } from "react-redux";
import { ThemeProvider } from "styled-components";
import GA4React from "ga-4-react";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";

import { RootState } from "../../core/store/types";
import { Flex } from "../../core/styles";
import Button from "../Button";
import Icon from "../Icon";
import {
  BannerButtonControls,
  BannerContainer,
  BannerHeader,
  BannerText,
} from "./styled";
import { darkTheme, lightTheme } from "../../core/styles/theme/colors";
import { themes } from "../../core/styles/theme";

const GA = new GA4React(process.env.REACT_APP_ANALYTICS_KEY, {
  debug_mode: !process.env.production,
});

function CookieBanner(): JSX.Element {
  const [isCookie, setIsCookie] = React.useState<boolean>(false);
  const [isBannerVisible, setIsBannerVisible] = React.useState<boolean>(false);

  const { theme } = useSelector(
    ({ settingsReducer }: RootState) => settingsReducer
  );

  const themeObject = {
    ...themes,
    color: theme === "light" ? darkTheme : lightTheme,
  };

  React.useEffect(() => {
    const permission: string | undefined = Cookies.get(
      "cookie_tracking_permission"
    );

    if (permission) {
      const getBooleanValue = !!(permission && permission === "true");
      setIsCookie(getBooleanValue);
      setIsBannerVisible(false);
    } else {
      setIsBannerVisible(true);
    }
  }, []);

  const intializeAnalytics = React.useCallback(async () => {
    if (GA4React.isInitialized()) return;
    GA.initialize();
  }, []);

  React.useEffect(() => {
    if (isCookie) {
      intializeAnalytics();
    }
  }, [intializeAnalytics, isCookie]);

  function handleCookieConsent(status: string) {
    const getBooleanValue = !!(status && status === "true");
    Cookies.set("cookie_tracking_permission", status, { expires: 365 });
    setIsCookie(getBooleanValue);
    setIsBannerVisible(false);
    if (getBooleanValue === true) {
      setTimeout(() => GA.gtag("event", "cookies", "accepted"), 3000);
    }
  }

  if (!isBannerVisible) {
    return null;
  }

  return (
    <ThemeProvider theme={themeObject}>
      <BannerContainer>
        <Flex justifyContent="space-between" flex="1">
          <BannerHeader>We use cookies!</BannerHeader>
          <Icon
            name="deleteIcon"
            size={6}
            onClick={() => handleCookieConsent("false")}
            style={{
              marginLeft: 20,
              marginRight: 0,
              color: "#7A787C",
              cursor: "pointer",
            }}
          />
        </Flex>
        <BannerText>
          To provide you with the best experience in the future, we&apos;d like
          to keep track of how you use our website. If you wish to know more,
          read our <Link to="/privacy">privacy policy</Link>. <br />
          We do this anonymously, and we don&apos;t share this information with
          third parties.
        </BannerText>
        <BannerButtonControls>
          <Button onClick={() => handleCookieConsent("true")}>I agree</Button>
          <Button
            style={{ backgroundColor: "#EBE4FF" }}
            onClick={() => handleCookieConsent("false")}
          >
            No thanks
          </Button>
        </BannerButtonControls>
      </BannerContainer>
    </ThemeProvider>
  );
}

export { CookieBanner };
